import React from 'react'
import { BiCheck, BiErrorCircle, BiInfoCircle, BiError } from 'react-icons/bi'

export type AlertType = 'success' | 'error' | 'warning' | 'info'

export interface AlertProps {
  type: AlertType
  title?: string
  message: string
  onClose?: () => void
  onDismiss?: () => void
  onAction?: () => void
  actionLabel?: string
  className?: string
  testId?: string
}

const Alert: React.FC<AlertProps> = ({
  type,
  title,
  message,
  onClose,
  onDismiss,
  onAction,
  actionLabel,
  className = '',
  testId
}) => {
  const handleDismiss = onClose || onDismiss

  const getAlertStyles = () => {
    switch (type) {
      case 'success':
        return {
          container: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
          icon: 'text-green-400',
          title: 'text-green-800 dark:text-green-200',
          message: 'text-green-700 dark:text-green-300',
          button: 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-700',
          dismiss: 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200'
        }
      case 'warning':
        return {
          container: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
          icon: 'text-yellow-400',
          title: 'text-yellow-800 dark:text-yellow-200',
          message: 'text-yellow-700 dark:text-yellow-300',
          button: 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-700',
          dismiss: 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200'
        }
      case 'info':
        return {
          container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
          icon: 'text-blue-400',
          title: 'text-blue-800 dark:text-blue-200',
          message: 'text-blue-700 dark:text-blue-300',
          button: 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-700',
          dismiss: 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200'
        }
      default: // error
        return {
          container: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
          icon: 'text-red-400',
          title: 'text-red-800 dark:text-red-200',
          message: 'text-red-700 dark:text-red-300',
          button: 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700',
          dismiss: 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200'
        }
    }
  }

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <BiCheck className="h-5 w-5" />
      case 'warning':
        return <BiError className="h-5 w-5" />
      case 'info':
        return <BiInfoCircle className="h-5 w-5" />
      default: // error
        return <BiErrorCircle className="h-5 w-5" />
    }
  }

  const styles = getAlertStyles()

  return (
    <div
      className={`mb-6 border rounded-lg p-4 ${styles.container} ${className}`}
      data-testid={testId}
    >
      <div className="flex items-start">
        <div className="flex-1 flex justify-between items-center">
          <div className="flex items-center">
            <div className={`flex-shrink-0 ${styles.icon}`}>
              {getIcon()}
            </div>
            <div className="ml-3 flex flex-col justify-center">
              {title && (
                <h3 className={`text-sm font-medium ${styles.title} mb-1`}>
                  {title}
                </h3>
              )}
              <div className={`text-sm ${styles.message}`} data-testid="alert-message">
                <p>{message}</p>
              </div>
              {onAction && actionLabel && (
                <div className="mt-4">
                  <button
                    onClick={onAction}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${styles.button}`}
                    data-testid="alert-action-button"
                  >
                    {actionLabel}
                  </button>
                </div>
              )}
            </div>
          </div>
          {handleDismiss && (
            <button
              onClick={handleDismiss}
              className={`text-sm transition-colors ${styles.dismiss} ml-4`}
              data-testid="alert-dismiss-button"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Alert 